import {CustomFieldType} from '@frontapp/workflows/custom_fields';
import {DateTime} from 'luxon';
import React, {ComponentPropsWithoutRef, FC} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {useLocalize} from '../../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {
  defineMessage,
  Trans,
} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useEventHandler} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {FrontKeyboardEvent} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {InboxModel} from '../../../../../../../libs/shared/core/src/models/api/inboxModel';
import {inboxHasColor} from '../../../../../../../libs/shared/core/src/models/helpers/inboxHelpers';
import {getTimeFormat} from '../../../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {boxedInputColorReaders} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {DropdownBoxCoordinator} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {NumberInputBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {SmartDateTimeFormatter} from '../../../../../../../libs/shared/ui/src/components/datetime/smartDateTimeFormatter';
import {Dropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {ColorDot} from '../../../../../../../libs/shared/ui/src/components/utility/colorDot';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {buildPublicId, tryParsePublicId} from '../../../../../../models/applications/publicIdTypes';
import {getInboxes} from '../../../../../../store/data/inboxes/inboxesSelectors';
import {DateTimePicker} from '../../../../../common/dateTime/dateTimePicker';
import {SearchInboxDropdownContainer} from '../../../../../common/dropdowns/search/searchInboxDropdownContainer';
import {InlineEditingBooleanPopover} from '../../../../../common/inlineEditing/inlineEditingBooleanPopover';
import {InlineEditingCustomAttribute} from '../../../../../common/inlineEditing/inlineEditingCustomAttribute';
import {InlineEditingEnumDropdown} from '../../../../../common/inlineEditing/inlineEditingEnumDropdown';
import {InlineEditingTextBox} from '../../../../../common/inlineEditing/inlineEditingTextBox';
import {SingleSelectTeammateField} from './singleSelectTeammateField';

/*
 * Styles.
 */

const StyledPlaceholderSpan = styled.span`
  color: ${boxedInputColorReaders.textPlaceholder};
`;

// This lets us reuse inline editing components that come with a `left: -8px; margin-top: 1px;` style.
const StyledShiftedDiv = styled.div`
  padding-left: 8px;
  padding-right: 8px;
  margin-top: -2px; /* Why 2px and not 1px? Because we are in a flex-box with align-items: center. */
  height: 30px;
`;

const StyledFlexDiv = styled.div`
  display: flex;
`;

const StyledMinWidthDiv = styled.div`
  min-width: 112px;
`;

const StyledColorDotDiv = styled.div`
  padding: 6px 8px 6px 4px;
`;

/*
 * Components.
 */

export const CustomAttributeValueEditor: FC<ComponentPropsWithoutRef<typeof InlineEditingCustomAttribute>> = (
  props,
) => {
  const localize = useLocalize();
  const timeFormat = useAppSelector(getTimeFormat);
  const inboxes = useAppSelector(getInboxes);
  const theme = useUserTheme();

  const ignoreEnterOnKeyDown = useEventHandler((event: FrontKeyboardEvent) => {
    switch (event.key) {
      case Key.Enter:
        event.preventDefault();
        event.stopPropagation();
        break;

      default:
      // Do nothing.
    }
  });

  switch (props.customAttribute.customField.type) {
    case CustomFieldType.TEAMMATE:
      return (
        <SingleSelectTeammateField
          selectedTeammateAlias={props.editedValue || undefined}
          onChange={(teammate) => props.onEdit(teammate.alias)}
        />
      );

    case CustomFieldType.NUMBER:
      return (
        <StyledMinWidthDiv>
          <NumberInputBox
            size={VisualSizesEnum.MEDIUM}
            value={props.editedValue ? Number(props.editedValue) : ''}
            onChange={(value) => props.onEdit(value.toString())}
            placeholder={localize(
              defineMessage({id: 'search-query-builder-custom-field-placeholder', message: 'Enter value'}),
            )}
            onKeyDown={ignoreEnterOnKeyDown}
            noMargin
          />
        </StyledMinWidthDiv>
      );

    case CustomFieldType.INBOX: {
      const selectedInboxId = tryParsePublicId(props.editedValue, 'inbox');
      const selectedInbox = isDefined(selectedInboxId) ? inboxes[selectedInboxId] : undefined;
      const color = selectedInbox && inboxHasColor(selectedInbox) ? selectedInbox.color : theme.greys.shade50;

      return (
        <DropdownBoxCoordinator
          value={
            selectedInboxId ? (
              <StyledFlexDiv>
                <StyledColorDotDiv>
                  <ColorDot color={color} size={8} />
                </StyledColorDotDiv>
                {selectedInbox?.name ?? props.editedValue}
              </StyledFlexDiv>
            ) : (
              <StyledPlaceholderSpan>
                <Trans id="search-query-builder-custom-field-placeholder-inbox">Select inbox</Trans>
              </StyledPlaceholderSpan>
            )
          }
          renderDropdown={(popoverProps) => (
            <SearchInboxDropdownContainer
              {...popoverProps}
              onChange={(inbox: InboxModel): void => {
                props.onEdit(buildPublicId('inbox', inbox.id));
              }}
              selectedInboxIds={selectedInboxId ? [selectedInboxId] : []}
            />
          )}
          shouldDisablePadding
        />
      );
    }

    case CustomFieldType.DATETIME: {
      const selectedDateTime = props.editedValue ? DateTime.fromMillis(Number(props.editedValue)) : undefined;

      const buttonValue = !selectedDateTime ? (
        <StyledPlaceholderSpan>
          <Trans id="search-query-builder-custom-field-placeholder-date">Select date</Trans>
        </StyledPlaceholderSpan>
      ) : (
        <SmartDateTimeFormatter value={selectedDateTime.valueOf()} />
      );

      return (
        <DropdownBoxCoordinator
          value={buttonValue}
          renderDropdown={(popoverProps) => (
            <Dropdown {...popoverProps}>
              <DateTimePicker
                timeFormat={timeFormat}
                selectedDateTime={selectedDateTime}
                onDateTimeSelect={(dt: DateTime | undefined) => {
                  if (!dt) {
                    props.onEdit('');
                    return;
                  }

                  props.onEdit(dt.toMillis().toString());
                }}
              />
            </Dropdown>
          )}
          shouldDisablePadding
        />
      );
    }

    case CustomFieldType.BOOLEAN:
      return (
        <StyledShiftedDiv>
          <InlineEditingBooleanPopover
            {...props}
            value={props.editedValue}
            customPlaceholder={
              <StyledPlaceholderSpan>
                <Trans id="search-query-builder-custom-field-placeholder-bool">Select value</Trans>
              </StyledPlaceholderSpan>
            }
          />
        </StyledShiftedDiv>
      );

    case CustomFieldType.ENUM:
      return (
        <StyledShiftedDiv>
          <InlineEditingEnumDropdown
            {...props}
            customField={props.customAttribute.customField}
            value={props.editedValue}
            customPlaceholder={
              <StyledPlaceholderSpan>
                <Trans id="search-query-builder-custom-field-placeholder-enum">Select value</Trans>
              </StyledPlaceholderSpan>
            }
          />
        </StyledShiftedDiv>
      );

    case CustomFieldType.STRING:
      return (
        <StyledShiftedDiv>
          <StyledMinWidthDiv>
            <InlineEditingTextBox {...props} value={props.editedValue} />
          </StyledMinWidthDiv>
        </StyledShiftedDiv>
      );

    case CustomFieldType.TEAMMATE_LIST:
      return null;

    default:
      props.customAttribute.customField.type satisfies never;
      return null;
  }
};
